/**
 * « Moyen de paiement » (SPEC MC1) — maquette 17-compte (`tag solid` + •••• 4242 + « Modifier ma
 * carte »). Logique = UserProfile.tsx PaymentMethod() : enregistrement POST client/paymentinfo (numéro
 * et cryptogramme réduits aux chiffres), mêmes messages d'erreur.
 *
 * Carte lue dans GET client/me (fiche du client, déjà chargée par Mon compte) et plus dans GET
 * client/completeinfo (retour de Ségolène du 07/10, vitesse) : même objet ClientCreditCard (vérifié le
 * 07/10 en lecture sur un compte de la prod : mêmes clés, mêmes valeurs, numéro masqué), un appel
 * Mindbody de moins (client/clientcompleteinfo : le plus lent de l'écran, 0,6 à 0,8 s, jamais mis en
 * miroir). Après enregistrement : fiche mise à jour en cache, completeinfo invalidé (paiement).
 */
import { Input } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useFetchMe } from 'hooks/useApiQueries';
import { produce } from 'immer';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import ReactInputMask from 'react-input-mask';
import type { MindbodyUser } from 'types/client';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP } from 'utils/http';
import { parseMindbodyError } from 'utils/mindbodyError';
import { resolveUserError } from 'utils/userErrorMessages';
import { cardExpiryMaskProps } from 'v2/lib/cardExpiry';
import { Icon } from './Icon';
import { Notice } from './Ui';

interface PaymentMethodData {
  CreditCardNumber: string;
  BillingName: string;
  Exp: string;
  Cryptogram: string;
}

export function PaymentMethodCard() {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  /*
   * Focus gardé (revue du 05/10, même correctif que le mot de passe) : le bouton « Modifier ma carte » est REMPLACÉ par
   * le formulaire, et « Annuler » démonte le formulaire : le focus tombait sur BODY. À l'ouverture, 1er champ ; à la
   * fermeture (Annuler, enregistrement), retour au bouton.
   */
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);
  useEffect(() => {
    if (isEditing) document.getElementById('mmv2-acct-cc-name')?.focus();
    else if (wasEditing.current) editButtonRef.current?.focus();
    wasEditing.current = isEditing;
  }, [isEditing]);
  const { data: user, isLoading } = useFetchMe();
  const card = user?.ClientCreditCard;
  const hasPaymentMethodSetup = !!card?.CardNumber;

  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<PaymentMethodData>();

  // = PaymentMethod() onSubmit
  async function onSubmit(data: PaymentMethodData) {
    const sanitized: PaymentMethodData = {
      ...data,
      CreditCardNumber: data.CreditCardNumber.replace(/\D/g, ''),
      Cryptogram: data.Cryptogram.replace(/\D/g, ''),
    };

    try {
      const response = await HTTP.post<MindbodyUser>('/client/paymentinfo', sanitized).send();

      queryClient.setQueriesData<MindbodyUser>({ queryKey: ['/client/me'] }, (current) =>
        current
          ? produce(current, (draft) => {
              draft.ClientCreditCard = response.ClientCreditCard;
            })
          : current,
      );
      queryClient.invalidateQueries({ queryKey: ['/client/completeinfo'] });
      reset();
      setIsEditing(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2400);
    } catch (error) {
      if (error instanceof ApiClientException) {
        setError('root', { message: parseMindbodyError(error.Message) });
        return null;
      }
      const { title, description } = resolveUserError('paymentMethod', error);
      setError('root', { message: description ? `${title}. ${description}` : title });
      return null;
    }
  }

  const last4 = card?.LastFour || card?.CardNumber?.replace(/\D/g, '').slice(-4) || '';

  return (
    <section className="mmv2-card mmv2-card--pad mmv2-acct-side" aria-labelledby="mmv2-acct-payment">
      <h2 className="mmv2-acct-kicker" id="mmv2-acct-payment">
        Moyen de paiement
      </h2>

      {showSuccess ? (
        <div className="mmv2-acct-inline-ok" role="status">
          <Notice tone="success">Carte mise à jour.</Notice>
        </div>
      ) : null}

      {isLoading ? (
        <div className="mmv2-acct-skeleton mmv2-acct-skeleton--line" aria-busy="true" aria-label="Chargement" />
      ) : !isEditing ? (
        <>
          {hasPaymentMethodSetup ? (
            <div className="mmv2-acct-paycard">
              <span className="mmv2-tag mmv2-tag--solid">{card?.CardType || 'Carte'}</span>
              <b className="mmv2-acct-paycard__num" aria-label={`Carte se terminant par ${last4}`}>
                •••• {last4}
              </b>
              <span className="mmv2-small mmv2-muted mmv2-acct-paycard__exp">
                Exp. {card?.ExpMonth}/{card?.ExpYear}
              </span>
              {card?.CardHolder ? <span className="mmv2-small mmv2-muted mmv2-acct-paycard__holder">{card.CardHolder}</span> : null}
            </div>
          ) : (
            <p className="mmv2-small mmv2-muted mmv2-acct-side__intro">Aucune carte enregistrée.</p>
          )}
          <button
            type="button"
            className="mmv2-btn mmv2-btn--ghost mmv2-btn--sm mmv2-btn--block mmv2-acct-side__cta"
            ref={editButtonRef}
            onClick={() => setIsEditing(true)}
          >
            <Icon name="card" size={15} />
            {hasPaymentMethodSetup ? 'Modifier ma carte' : 'Ajouter une carte'}
          </button>
        </>
      ) : (
        <form id="mmv2-acct-payform" className="mmv2-acct-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="mmv2-field mmv2-acct-field">
            <label className="mmv2-label" htmlFor="mmv2-acct-cc-name">
              Nom du titulaire
            </label>
            <input
              id="mmv2-acct-cc-name"
              className="mmv2-input"
              autoComplete="cc-name"
              aria-invalid={!!errors.BillingName}
              {...register('BillingName', { required: true })}
            />
          </div>
          <div className="mmv2-field mmv2-acct-field">
            <label className="mmv2-label" htmlFor="mmv2-acct-cc-num">
              Numéro de carte
            </label>
            <Input
              id="mmv2-acct-cc-num"
              className="mmv2-input"
              inputMode="numeric"
              autoComplete="cc-number"
              aria-invalid={!!errors.CreditCardNumber}
              {...register('CreditCardNumber', { pattern: /\d\d\d\d \d\d\d\d \d\d\d\d \d\d\d\d/, required: true })}
              as={ReactInputMask}
              mask="9999 9999 9999 9999"
              maskChar={null}
            />
          </div>
          <div className="mmv2-acct-form__row">
            <div className="mmv2-field mmv2-acct-field">
              <label className="mmv2-label" htmlFor="mmv2-acct-cc-exp">
                Expiration
              </label>
              <Input
                id="mmv2-acct-cc-exp"
                className="mmv2-input"
                inputMode="numeric"
                autoComplete="cc-exp"
                placeholder="MM / AA"
                aria-invalid={!!errors.Exp}
                {...register('Exp', { pattern: /[0-1][0-9] \/ [0-9][0-9]/, required: true })}
                as={ReactInputMask}
                mask="99 / 99"
                maskChar={null}
                {...cardExpiryMaskProps}
              />
            </div>
            <div className="mmv2-field mmv2-acct-field">
              <label className="mmv2-label" htmlFor="mmv2-acct-cc-cvc">
                Cryptogramme
              </label>
              <Input
                id="mmv2-acct-cc-cvc"
                className="mmv2-input"
                inputMode="numeric"
                autoComplete="cc-csc"
                aria-invalid={!!errors.Cryptogram}
                {...register('Cryptogram', { pattern: /[0-9][0-9][0-9]/, required: true })}
                as={ReactInputMask}
                mask="9999"
                maskChar={null}
              />
            </div>
          </div>
          {errors.root ? (
            <p className="mmv2-acct-error" role="alert">
              {errors.root.message}
            </p>
          ) : null}
          <div className="mmv2-acct-actions">
            <button type="submit" className="mmv2-btn mmv2-btn--dark mmv2-btn--sm" disabled={isSubmitting} aria-busy={isSubmitting}>
              {isSubmitting ? 'Enregistrement…' : 'Enregistrer'}
            </button>
            <button
              type="button"
              className="mmv2-btn mmv2-btn--quiet mmv2-btn--sm"
              disabled={isSubmitting}
              onClick={() => {
                reset();
                setIsEditing(false);
              }}
            >
              Annuler
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
